import PropTypes from "prop-types";
import { LazyLoadImage } from "react-lazy-load-image-component";
import ReelModal from "../reel-modal/ReelModal";
import { Fragment, useEffect, useState, useRef } from "react";
import { isValidYoutubeUrl } from "../../shared/commonUtils";

const HeroSliderThreeSingle = ({ data }) => {
  const [open, setOpen] = useState(false);
  const [isVideo, setIsVideo] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({
    width: 1600,
    height: 600,
  });
  const imageContainerRef = useRef(null);

  // Function to generate optimized image URL - only replace width and height
  const generateOptimizedImageUrl = (baseUrl, width, height) => {
    if (!baseUrl) return "";

    // Extract the original URL from CDN URL if it exists
    const cdnPattern =
      /https:\/\/dukany\.io\/cdn-cgi\/image\/([^\/]+)\/(https:\/\/.+)/;
    const match = baseUrl?.match(cdnPattern);

    if (match) {
      // If it's already a CDN URL, extract existing parameters and original URL
      const existingParams = match[1];
      const originalUrl = match[2];

      // Replace width and height in existing parameters
      let newParams = existingParams.replace(/width=\d+/, `width=${width}`);
      // .replace(/height=\d+/, `height=${height}`)
      // .replace(/height=auto/, `height=${height}`);

      // If width or height don't exist, add them
      if (!newParams.includes("width=")) {
        newParams = `width=${width},${newParams}`;
      }
      if (!newParams.includes("height=")) {
        newParams = `${newParams},height=${height}`;
      }

      return `https://dukany.io/cdn-cgi/image/${newParams}/${originalUrl}`;
    } else {
      // If it's not a CDN URL, create a basic CDN URL with width and height only
      return `https://dukany.io/cdn-cgi/image/width=${width},height=${height},fit=cover,format=webp,quality=60,sharpen=1.0,dpr=2,slow-connection-quality=50/${baseUrl}`;
    }
  };

  // Calculate image dimensions based on container
  useEffect(() => {
    const updateDimensions = () => {
      if (imageContainerRef.current) {
        const containerWidth =
          imageContainerRef.current.offsetWidth || window.innerWidth;
        // Calculate height based on aspect ratio or use auto
        const containerHeight = Math.round(containerWidth * 0.6); // Adjust aspect ratio as needed

        setImageDimensions({
          width: Math.round(containerWidth),
          height: containerHeight,
        });
      }
    };

    // Initial calculation
    updateDimensions();

    // Update on resize with debounce
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateDimensions, 150);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  useEffect(() => {
    // Check if screen is mobile (≤ 700px)
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 700);
    };

    // Initial check
    checkIsMobile();

    // Add resize listener
    window.addEventListener("resize", checkIsMobile);

    // Determine if we should show video
    const shouldShowVideo = () => {
      if (isMobile) {
        return !!data.mobile_video;
      } else {
        return !!data.video;
      }
    };

    setIsVideo(shouldShowVideo());

    // Cleanup
    return () => window.removeEventListener("resize", checkIsMobile);
  }, [data, isMobile]);

  // Generate responsive srcSet for better browser optimization
  const generateSrcSet = (baseUrl, baseWidth) => {
    const sizes = [1, 1.5, 2]; // Different DPR values
    return sizes
      .map((multiplier) => {
        const width = Math.ceil(baseWidth * multiplier);
        const height = "auto";
        // const height = Math.ceil(imageDimensions.height * multiplier);
        const url = generateOptimizedImageUrl(baseUrl, width, height);
        return `${url} ${multiplier}x`;
      })
      .join(", ");
  };

  const desktopImageUrl = generateOptimizedImageUrl(
    data.image,
    imageDimensions.width,
    imageDimensions.height
  );

  const mobileImageUrl = generateOptimizedImageUrl(
    data.mobile_image || data.image,
    imageDimensions.width,
    imageDimensions.height
  );

  return (
    <div className="mobile-banner slider-height-7- bg-glaucous d-flex-1 align-items-center- ">
      <div className="">
        <div className="row- slider-h9-mrg--">
          <div>
            <div
              ref={imageContainerRef}
              className="slider-singleimg-hm9 single-img-banner custom-test text-center"
            >
              <Fragment>
                <picture className="single-img-banner">
                  <source
                    media="(max-width: 700px)"
                    srcSet={generateSrcSet(
                      data.mobile_image || data.image,
                      imageDimensions.width
                    )}
                    src={mobileImageUrl}
                  />
                  <source
                    media="(min-width: 701px)"
                    srcSet={generateSrcSet(data.image, imageDimensions.width)}
                    src={desktopImageUrl}
                  />
                  <img
                    style={{
                      cursor:
                        isValidYoutubeUrl(data.url) &&
                        data.url &&
                        data.url !== "" &&
                        "pointer",
                      width: "100%",
                      height: "auto",
                      maxWidth: "100%",
                    }}
                    onClick={() => setOpen(true)}
                    alt={window.businessByDomain + "-hero-slider"}
                    className="desktop-img-banner"
                    src={desktopImageUrl}
                    width={imageDimensions.width}
                    height={375}
                    // height={imageDimensions.height}
                    fetchPriority="high"
                  />
                </picture>
              </Fragment>

              {/* Uncomment if you want to enable video */}
              {/* {isVideo && (
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  style={{ width: "100%", height: "auto" }}
                >
                  <source
                    src={isMobile ? data.mobile_video : data.video}
                    type="video/mp4"
                  />
                </video>
              )} */}
            </div>
          </div>
        </div>
      </div>
      {isValidYoutubeUrl(data.url) && data.url && data.url !== "" && (
        <ReelModal
          open={open}
          onClose={() => setOpen(false)}
          youtubeId={data.url}
        />
      )}
    </div>
  );
};

HeroSliderThreeSingle.propTypes = {
  data: PropTypes.shape({
    image: PropTypes.string,
    mobile_image: PropTypes.string,
    video: PropTypes.string,
    mobile_video: PropTypes.string,
    url: PropTypes.string,
  }),
};

export default HeroSliderThreeSingle;
